import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import logoImage from '/assets/images/logo-volaptic-1775131861970.png';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, signOut, isAdmin } = useAuth();
  const isLoginPage = location?.pathname === '/login-form';
  const isDashboard = location?.pathname === '/professional-dashboard' || location?.pathname === '/cliente/dashboard';
  const [userRole, setUserRole] = React.useState(null);
  const [avatarUrl, setAvatarUrl] = React.useState(null);
  const [fullName, setFullName] = React.useState(null);

  // Fetch user role from database
  React.useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        try {
          const { data: userData, error } = await supabase
            ?.from('users')
            ?.select('role, avatar_url, full_name')
            ?.eq('id', user?.id)
            ?.single();
          
          if (!error && userData) {
            setUserRole(userData?.role);
            setAvatarUrl(userData?.avatar_url || null);
            setFullName(userData?.full_name || null);
          }
        } catch (err) {
          console.error('Error fetching user role:', err);
        }
      } else {
        setUserRole(null);
        setAvatarUrl(null);
        setFullName(null);
      }
    };
    fetchUserRole();
  }, [user, location?.pathname]);

  React.useEffect(() => {
    const handler = () => {
      if (user) {
        supabase
          ?.from('users')
          ?.select('role, avatar_url, full_name')
          ?.eq('id', user?.id)
          ?.single()
          ?.then(({ data }) => {
            if (data) {
              setUserRole(data?.role);
              setAvatarUrl(data?.avatar_url || null);
              setFullName(data?.full_name || null);
            }
          });
      }
    };
    window.addEventListener('volaptic:profile-updated', handler);
    return () => window.removeEventListener('volaptic:profile-updated', handler);
  }, [user]);

  const handleLogout = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login-form');
    }
  };

  const getDashboardLink = () => {
    if (userRole === 'cliente') {
      return '/cliente/dashboard';
    }
    return '/professional-dashboard';
  };

  const getRoleLabel = () => {
    if (userRole === 'cliente') {
      return 'Cliente';
    }
    return null;
  };

  return (
    <header
      className="header-nav"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', width: '100%' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Link to="/" className="header-logo flex items-center">
          <img
            src={logoImage}
            alt="Volaptic"
            style={{ height: '56px', width: 'auto' }}
          />
        </Link>
        {(userRole === 'superadmin' || userRole === 'admin') && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '4px 11px',
            borderRadius: '999px',
            background: userRole === 'superadmin' ? '#104d98' : '#32b3e5',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.02em',
            flexShrink: 0,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            {userRole === 'superadmin' ? 'Super Admin' : 'Admin'}
          </span>
        )}
      </div>
      <div className="header-actions">
        {!loading && (
          user ? (
            <div className="flex items-center gap-3">
              {(isAdmin || userRole === 'admin' || userRole === 'superadmin') ? (
                <>
                  {(isAdmin || userRole === 'admin' || userRole === 'superadmin') && (
                    <select
                      value={typeof window !== 'undefined' ? window.location?.pathname : '/'}
                      onChange={(e) => navigate(e?.target?.value)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        background: '#f9fafb',
                        cursor: 'pointer',
                        maxWidth: '200px',
                      }}
                    >
                      <option value="/">🏠 Home</option>
                      <optgroup label="Professionista">
                        <option value="/professional-dashboard">📊 Dashboard</option>
                        <option value="/professional/settings">⚙️ Impostazioni</option>
                        <option value="/professional/trattative">Trattative</option>
                      </optgroup>
                      <optgroup label="Cliente">
                        <option value="/cliente/dashboard">👤 Dashboard</option>
                        <option value="/cliente/settings">⚙️ Impostazioni</option>
                        <option value="/cliente/richiesta/nuova">Nuova Richiesta</option>
                      </optgroup>
                      <optgroup label="Admin">
                        <option value="/admin">🛡️ Overview</option>
                        <option value="/admin/clienti">Clienti</option>
                        <option value="/admin/professionisti">Professionisti</option>
                        <option value="/admin/richieste">Richieste</option>
                        <option value="/admin/preventivi">Preventivi</option>
                        <option value="/admin/audit-log">Audit</option>
                        <option value="/admin/settings">Impostazioni</option>
                      </optgroup>
                      <optgroup label="Auth">
                        <option value="/login-form">Login</option>
                        <option value="/signup-form">Signup</option>
                        <option value="/payment-verification">Payment</option>
                      </optgroup>
                    </select>
                  )}
                  <Link to="/professional-dashboard">
                    <Button variant="ghost" size="default" iconName="LayoutDashboard">
                      Dashboard
                    </Button>
                  </Link>
                </>
              ) : (
                !isDashboard && (
                  <Link to={getDashboardLink()}>
                    <Button variant="ghost" size="default" iconName="LayoutDashboard">
                      Dashboard
                    </Button>
                  </Link>
                )
              )}
              <div className="flex items-center gap-3">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      objectFit: 'cover', flexShrink: 0,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div
                    style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ color: '#fff', fontSize: '12px', fontWeight: 700, lineHeight: 1 }}>
                      {(fullName || user?.email || '?')?.split(/[\s@]+/)?.filter(Boolean)?.slice(0, 2)?.map(p => p?.[0]?.toUpperCase())?.join('') || '?'}
                    </span>
                  </div>
                )}
                <span className="text-sm font-semibold text-primary">
                  {fullName || user?.email?.split('@')?.[0]}
                </span>
              </div>
              <Button variant="outline" size="default" onClick={handleLogout}>
                Esci
              </Button>
            </div>
          ) : (
            !isLoginPage && (
              <div className="flex items-center gap-3">
                <a
                  href="/#come-funziona"
                  className="hidden md:inline text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  Come funziona
                </a>
                <Link to="/signup-form">
                  <button
                    style={{ backgroundColor: 'var(--color-primary)' }}
                    className="text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
                  >
                    Sono un Professionista
                  </button>
                </Link>
                <Link
                  to="/registrati-cliente"
                  className="hidden md:inline-flex items-center border px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-primary) 10%, transparent)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Cerco un Professionista
                </Link>
                <Link
                  to="/login-form"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Accedi
                </Link>
              </div>
            )
          )
        )}
      </div>
    </header>
  );
};

export default Header;